import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const AuthContext = createContext(null);
const TOKEN_KEY = "sst_access_token";

// Always attach token from localStorage to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// ─── 401 Auto-Refresh Interceptor ─────────────────────────────────────────────
// When ANY API call returns 401 (typically an expired 24-hour access token),
// silently call /api/auth/refresh using the httpOnly refresh-token cookie to
// get a fresh access token, then retry the original request ONCE.
//
// Concurrent requests: if multiple calls return 401 at the same time, we serialize
// them through a single in-flight promise so /refresh is only hit once per burst.
//
// On refresh failure (refresh token expired, invalid, or refresh endpoint itself
// errors), we clear localStorage and emit a global event that the App handles
// by navigating to /login with a "session expired" message. This is the ONLY
// scenario where the user is logged out — normal token expiry during active use
// is now silent.
let _refreshInFlight = null;          // shared promise during concurrent 401s
let _hasNotifiedSessionExpired = false; // prevent duplicate toasts/redirects

function _notifySessionExpired() {
  if (_hasNotifiedSessionExpired) return;
  _hasNotifiedSessionExpired = true;
  localStorage.removeItem(TOKEN_KEY);
  try {
    window.dispatchEvent(new CustomEvent("auth:session-expired"));
  } catch { /* no-op */ }
}

async function _doRefresh() {
  // [auth-debug] Log refresh attempts so production failures can be diagnosed
  // without server access. Visible in browser DevTools → Console.
  // eslint-disable-next-line no-console
  console.info("[auth] refresh → POST /api/auth/refresh");
  const resp = await axios.post(
    `${API}/auth/refresh`,
    {},
    { withCredentials: true, _skipAuthRetry: true } // marker prevents recursive 401 handling
  );
  const newToken = resp?.data?.access_token;
  if (!newToken) throw new Error("No access_token in refresh response");
  localStorage.setItem(TOKEN_KEY, newToken);
  // eslint-disable-next-line no-console
  console.info("[auth] refresh OK — new token stored");
  return newToken;
}

axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;

    // Only intercept 401, and never re-enter for the refresh call itself
    if (status !== 401 || original._skipAuthRetry || original._isRetry) {
      return Promise.reject(error);
    }
    // Don't try to refresh on login/register/refresh endpoints themselves
    const url = original.url || "";
    if (/\/auth\/(login|register|refresh)/.test(url)) {
      return Promise.reject(error);
    }

    // eslint-disable-next-line no-console
    console.warn("[auth] 401 caught → attempting silent refresh", { url });

    try {
      // Coalesce concurrent 401s onto a single refresh call.
      if (!_refreshInFlight) {
        _refreshInFlight = _doRefresh().finally(() => { _refreshInFlight = null; });
      }
      const newToken = await _refreshInFlight;
      // Retry the original request ONCE with the new token.
      original._isRetry = true;
      original.headers = original.headers || {};
      original.headers["Authorization"] = `Bearer ${newToken}`;
      // eslint-disable-next-line no-console
      console.info("[auth] retrying original request", { url });
      return axios(original);
    } catch (refreshErr) {
      // eslint-disable-next-line no-console
      console.error("[auth] refresh FAILED — redirecting to login", {
        url,
        refreshStatus: refreshErr?.response?.status,
        refreshDetail: refreshErr?.response?.data,
      });
      _notifySessionExpired();
      return Promise.reject(error);
    }
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      // No access token in localStorage — but the user may still have a valid
      // refresh cookie from a recent session. Try refresh once before declaring
      // them logged out (handles the "I closed the tab last night" case).
      _doRefresh()
        .then(() => axios.get(`${API}/auth/me`, { withCredentials: true }))
        .then((res) => setUser(res.data))
        .catch(() => setUser(false))
        .finally(() => setLoading(false));
      return;
    }
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => {
        // The 401 interceptor above already attempted refresh+retry. If we got
        // here, refresh failed too — clear stale token and treat as logged out.
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // Listen for the "session expired" event from the response interceptor so
  // the AuthContext state stays in sync with localStorage.
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("auth:session-expired", onExpired);
    return () => window.removeEventListener("auth:session-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    _hasNotifiedSessionExpired = false; // reset the "session expired" toast guard
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
    }
    try {
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data);
      return data;
    }
  };

  const register = async (email, password, name, center_name) => {
    _hasNotifiedSessionExpired = false;
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, center_name }, { withCredentials: true });
    // Sprint 2 Batch 3 — registration now returns needs_verification:true
    // instead of an access token. Caller (Register.js) handles the redirect.
    if (data.needs_verification) {
      return data;   // Don't set user — account is unverified
    }
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      return data;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    }
  };

  const formatError = (detail) => {
    if (!detail) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
    if (typeof detail === "object" && detail.message) return detail.message;
    return String(detail);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, formatError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
